import { addDays, afterShipment, lastsWeeksFor, planBoxes, workingCadences, type Box, type BoxLine, type RefillItem, type RefillRules } from '../core/rules/refillPlan'
import type { Subscription } from '../core/types/account'
import type { Product } from '../core/types/catalog'
import type { Policy } from '../core/types/site'

/** De regels voor slim bijvullen uit de instellingen. */
export const refillRules = (sub: Policy['subscription']): RefillRules => ({
  minBoxValue: sub.minBoxValue,
  maxUnitsPerProduct: sub.maxUnitsPerProduct,
  intervals: sub.intervals,
  bufferDays: sub.bufferDays,
})

/** Producten uit de winkel als bijvulregels: net binnen, dus op over "gaat ± X mee". Onbekend valt weg. */
export function refillItemsFromProducts(products: readonly { product: Product; qty?: number }[], today: string): RefillItem[] {
  return products.flatMap(({ product, qty = 1 }) => {
    const weeks = lastsWeeksFor(product.usesPerBottle)
    return weeks === null ? [] : [{ productId: product.id, price: product.price, lastsWeeks: weeks, runsOutOn: addDays(today, weeks * 7 * qty) }]
  })
}

/**
 * De bijvulregels in de winkelmand: welke ritmes het minimum per doos halen, en welk ritme er echt
 * meegaat met de bestelling (het gekozen als het werkt, anders het voorstel). Eén bron voor de mand,
 * de afrekenpagina en de demo-server, zodat ze nooit verschillen.
 */
export function cartRefill(
  lines: readonly { product: Product; qty: number; subscribe: boolean }[],
  chosenWeeks: number,
  policySub: Policy['subscription'],
  start: string,
) {
  const subscribed = lines.filter((l) => l.subscribe)
  const items = refillItemsFromProducts(subscribed.map((l) => ({ product: l.product, qty: l.qty })), start)
  const working = items.length ? workingCadences(items, refillRules(policySub), start) : []
  const weeks = working.includes(chosenWeeks) ? chosenWeeks : (working[0] ?? chosenWeeks)
  // Alleen producten zonder "gaat ± … mee": die gaan elke doos mee, dus hun som moet het minimum halen.
  const unknownOnlyTooSmall =
    subscribed.length > 0 && items.length === 0 && subscribed.reduce((s, l) => s + l.product.price * l.qty, 0) < policySub.minBoxValue
  return { items, working, weeks, tooSmall: (items.length > 0 && working.length === 0) || unknownOnlyTooSmall }
}

/**
 * Ritmes waaruit gekozen mag worden voor een lopend abonnement: wat een jaar lang het minimum haalt,
 * plus het huidige (nooit vastzetten). Haalt geen enkel ritme het (bijv. alleen producten zonder
 * "gaat ± … mee"), dan alle ritmes: dan kan het minimum niet beslissen.
 */
export function subscriptionCadences(sub: Subscription, productById: (id: string) => Product | undefined, policySub: Policy['subscription']): number[] {
  const { items, unknown } = subscriptionRefill(sub, productById, policySub, 1)
  const known = items.filter((i) => !unknown.includes(i.productId))
  const working = known.length ? workingCadences(known, refillRules(policySub), sub.nextDelivery) : []
  const base = working.length ? working : policySub.intervals
  return Array.from(new Set([...base, sub.intervalWeeks])).sort((a, b) => a - b)
}

/**
 * Na een verzonden bijvuldoos, in één stap (de server doet dit in één transactie): nieuwe
 * op-datums voor wat erin zat, "deze keer niet" leeg, de levering geteld (voor het cadeau)
 * en de volgende doos een ritme verder.
 */
export function shipSubscriptionBox(
  sub: Subscription,
  lines: readonly { productId: string; qty: number }[],
  shippedOn: string,
  productById: (id: string) => Product | undefined,
  policySub: Policy['subscription'],
): Subscription {
  const { items } = subscriptionRefill(sub, productById, policySub, 1)
  const boxLines = lines
    .filter((l) => items.some((i) => i.productId === l.productId))
    .map((l) => ({ productId: l.productId, qty: l.qty, price: productById(l.productId)?.price ?? 0 }))
  const outs = afterShipment(items, { date: shippedOn, lines: boxLines, total: boxLines.reduce((s, l) => s + l.price * l.qty, 0) })
  const from = shippedOn > sub.nextDelivery ? shippedOn : sub.nextDelivery
  return {
    ...sub,
    skipNext: [],
    deliveriesDone: sub.deliveriesDone + 1,
    nextDelivery: addDays(from, sub.intervalWeeks * 7),
    items: sub.items.map((i) => ({ ...i, runsOutOn: outs.get(i.productId) ?? i.runsOutOn })),
  }
}

/**
 * Welke komende doos het cadeau krijgt: de `untilGift`-ste doos die echt verstuurd wordt. Een
 * "geen doos" (alles deze keer niet) telt niet mee. `-1` als het buiten de lijst valt.
 */
export function giftBoxIndex(boxes: readonly Box[], untilGift: number | null | undefined): number {
  if (!untilGift || untilGift < 1) return -1
  let shipped = 0
  for (let i = 0; i < boxes.length; i++) {
    if ((boxes[i]?.lines.length ?? 0) > 0) shipped += 1
    if (shipped === untilGift) return i
  }
  return -1
}

export type SubscriptionRefill = {
  items: RefillItem[]
  /** De komende dozen; de eerste is de volgende levering. */
  boxes: Box[]
  /** Producten zonder "gaat ± X mee": die gaan elke doos mee, zoals vroeger. */
  unknown: string[]
}

/**
 * Het abonnement als slim bijvullen. Zonder op-datum (oudere data) schatten we die uit de vorige
 * levering: toen kwam het binnen, dus op na "gaat ± X mee".
 */
export function subscriptionRefill(sub: Subscription, productById: (id: string) => Product | undefined, policySub: Policy['subscription'], count = 4): SubscriptionRefill {
  const rules = refillRules(policySub)
  const lastDelivery = addDays(sub.nextDelivery, -sub.intervalWeeks * 7)
  const unknown: string[] = []
  const items: RefillItem[] = sub.items.flatMap((i) => {
    const product = productById(i.productId)
    if (!product) return []
    const known = lastsWeeksFor(product.usesPerBottle, i.usageWeeks ?? 0)
    if (known === null) unknown.push(i.productId)
    // Onbekend: elke doos één, zoals het oude abonnement. De buffer erbij, anders rondt de
    // berekening (ritme + buffer) / ritme op naar 2 en gaat er elke doos een flesje te veel mee.
    const lastsWeeks = known ?? sub.intervalWeeks + Math.ceil(policySub.bufferDays / 7)
    const runsOutOn = i.runsOutOn ?? addDays(lastDelivery, lastsWeeks * 7)
    return [{ productId: i.productId, price: product.price, lastsWeeks, runsOutOn }]
  })
  const extras: BoxLine[] = sub.extras.flatMap((id) => {
    const p = productById(id)
    return p ? [{ productId: id, qty: 1, price: p.price }] : []
  })
  const boxes = planBoxes(items, {
    intervalWeeks: sub.intervalWeeks,
    rules,
    earliest: sub.nextDelivery,
    firstBox: sub.nextDelivery,
    skip: sub.skipNext ?? [],
    extras,
    count,
  })
  return { items, boxes, unknown }
}
